/**
 * 接力仪式（design §11.12 P3a，ADR-0013）的纯逻辑：邀请默认附哪张照片、「剩 N 天」怎么说、
 * 那句话的字数行数检查。不碰 DOM 和 three.js，方便单测。
 */
import { LIMITS } from '@star-sea/shared';

/** 挑照片只需要这两个字段；PhotoDto 和更多字段的都收 */
interface Dated {
  /** 'YYYY-MM-DD'，字典序就是先后 */
  date: string;
  thumbs: boolean;
}

/**
 * 默认附的那颗：日期最早、有缩略图的；日期一样取数组里在前的（也就是添加顺序在前的）。
 * 都没有缩略图就取日期最早的任意一张；没有照片返回 null。
 * 放进回收站的照片由调用方先滤掉（galaxy.livePhotos()、isGone）。
 */
export function inviteDefaults<T extends Dated>(photos: readonly T[]): T | null {
  // reduce 里严格 <：平手时留下先来的
  const earlier = (a: T | null, b: T) => (a === null || b.date < a.date ? b : a);
  return photos.filter((p) => p.thumbs).reduce<T | null>(earlier, null) ?? photos.reduce<T | null>(earlier, null);
}

/** 最新的一颗：同 inviteDefaults，取日期最晚的 */
export function latestPhoto<T extends Dated>(photos: readonly T[]): T | null {
  const later = (a: T | null, b: T) => (a === null || b.date > a.date ? b : a);
  return photos.filter((p) => p.thumbs).reduce<T | null>(later, null) ?? photos.reduce<T | null>(later, null);
}

/** 「上一份邀请还在 · 剩 N 天」：不够一天说小时，不够一小时说分钟（至少 1 分钟），都向上取整 */
export function inviteLeft(expiresAt: string, now: number): string {
  const left = Math.max(0, Date.parse(expiresAt) - now);
  if (left >= 86_400_000) return `剩 ${Math.ceil(left / 86_400_000)} 天`;
  if (left >= 3_600_000) return `剩 ${Math.ceil(left / 3_600_000)} 小时`;
  return `剩 ${Math.max(1, Math.ceil(left / 60_000))} 分钟`;
}

/**
 * 那句话提交前的检查：空着合法（可以不附话）。
 * 字数按码点（同服务端的 [...s].length），行数按修剪、归一化换行后的 \n 数（同服务端的 passage）。
 */
export function checkInviteMessage(text: string): string | null {
  const t = text.replace(/\r\n?/g, '\n').trim();
  if (!t) return null;
  if ([...t].length > LIMITS.inviteMessageLength) return `那句话最多 ${LIMITS.inviteMessageLength} 字`;
  if (t.split('\n').length > LIMITS.inviteMessageLines) return `那句话最多 ${LIMITS.inviteMessageLines} 行`;
  return null;
}
